import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button } from '../components/button';
import { Heading, Subheading } from '../components/heading';
import { Text } from '../components/text';
import { Checkbox, CheckboxField } from '../components/checkbox'; // Corrected import
import { Switch } from '../components/switch'; // Add Switch import
import { ArrowTopRightOnSquareIcon, LockClosedIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon, ArrowPathIcon, ChevronDownIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid'; // Added ChevronDownIcon
import { PageLayout, useAuth } from '../App'; // Import useAuth hook instead of context
import { GoogleAuthProvider, getAuth, reauthenticateWithPopup } from 'firebase/auth'; // Import GoogleAuthProvider and getAuth
import { getFunctions, httpsCallable } from 'firebase/functions'; // Keep for potential future use or other functions
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../components/dropdown';

const kloudstaxMasterBillingAccount = "billingAccounts/014FE6-98F48C-4CBE9E";
const tdsynnexMasterBillingAccount = "billingAccounts/012AA3-E0AD1D-E6331E";

// --- Helper: Status Icon Component ---
const StatusIcon = ({ status, className = "" }) => {
  if (status === 'running') {
    return <div className={`size-4 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin ${className}`} />;
  }
  if (status === 'success') {
    return <CheckCircleIcon className={`size-5 text-green-500 ${className}`} />;
  }
  if (status === 'failed') {
    return <XCircleIcon className={`size-5 text-red-500 ${className}`} />;
  }
  return <div className={`size-4 rounded-full bg-gray-300 ${className}`} />; // pending
};

// --- Helper: Case-insensitive sort of array of objects ---
const sortArrayOfObjectsCaseInsensitive = (array, key) => {
  return array.sort((a, b) => a[key].localeCompare(b[key], undefined, { sensitivity: 'base' }));
};

// --- Helper: Case-sensitive sort of array of objects ---
const sortArrayOfObjectsCaseSensitive = (array, key) => {
  return array.sort((a, b) => a[key].localeCompare(b[key], undefined, { sensitivity: 'case' }));
};


// --- Helper: Paginated API Fetch ---
const fetchAllPages = async (baseUrl, accessToken, responseKey, queryParams = {}, deduplicationFn = null) => {
  let allResults = [];
  let nextPageToken = null;
  let pageCount = 0;
  const PAGE_SIZE = 500; // Lower page size helps with debugging
  
  // Only initialize deduplication tracking if a deduplication function is provided
  const seenItems = deduplicationFn ? new Set() : null;
  
  do {
    // Build URL with pagination parameters
    let url = new URL(baseUrl, window.location.origin);
    
    // Add pageSize if supported by the API
    url.searchParams.append('pageSize', PAGE_SIZE);
    
    // Add the nextPageToken if we have one
    if (nextPageToken) {
      url.searchParams.append('pageToken', nextPageToken);
    }
    
    // Add any additional query parameters
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });
    
    // Make the request
    const response = await fetch(url.toString(), {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    
    if (!response.ok) {
      console.error(`API request failed: ${response.status} - ${response.statusText}`);
      throw new Error(`API request failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Extract the results using the provided response key
    const results = data[responseKey] || [];
    console.log(`Fetched page ${pageCount} with ${results.length} results`);
    
    // Apply deduplication if a deduplication function was provided
    if (deduplicationFn) {
      let newItemsCount = 0;
      results.forEach(item => {
        // Use the provided function to extract a unique key
        const uniqueKey = deduplicationFn(item);
        
        if (!seenItems.has(uniqueKey)) {
          seenItems.add(uniqueKey);
          allResults.push(item);
          newItemsCount++;
        }
      });
      
      console.log(`Added ${newItemsCount} new unique items (skipped ${results.length - newItemsCount} duplicates)`);
    } else {
      // No deduplication, add all results
      allResults = [...allResults, ...results];
    }
    
    // Get the next page token if any
    nextPageToken = data.nextPageToken || null;
    pageCount++;
    
    // Log pagination progress
    if (nextPageToken) {
      console.log(`Moving to page ${pageCount + 1} with token: ${nextPageToken.substring(0, 20)}...`);
    }
    
  } while (nextPageToken);
  
  console.log(`Finished pagination. Total items: ${allResults.length}`);
  return allResults;
};

const PrerequisitesCheck = ({ onConfirm, prerequisitesMet }) => {
  const [hasNoCUDs, setHasNoCUDs] = useState(false);
  const [notInStartupProgram, setNotInStartupProgram] = useState(false);
  const [noMarketplaceSubscriptions, setNoMarketplaceSubscriptions] = useState(false);

  useEffect(() => {
    onConfirm(hasNoCUDs && notInStartupProgram && noMarketplaceSubscriptions);
  }, [hasNoCUDs, notInStartupProgram, noMarketplaceSubscriptions, onConfirm]);

  return (
    <div className="space-y-6">
      <Text className="text-gray-600">
        Before transferring your project billing, please confirm the following. Transferring projects with active CUDs or while enrolled in the Startup Program can lead to a gap in benefits and requires manual handling.
      </Text>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="pr-6">
            <label htmlFor="no-cuds" className="block text-sm/6 text-zinc-950 font-semibold">
              I confirm I have <span className="font-semibold">no active Committed Use Discounts (CUDs)</span> on any billing accounts in my organization.
            </label>
            <Text className="text-sm text-gray-500">
              You can check for CUDs in the <a href="https://console.cloud.google.com/billing/commitments" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 underline inline-flex items-center gap-1">GCP Billing Commitments section <ArrowTopRightOnSquareIcon className="h-4 w-4"/></a>.
            </Text>
          </div>
          <Switch
            checked={hasNoCUDs}
            onChange={setHasNoCUDs}
            color="cyan"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="pr-6">
            <label htmlFor="no-startup-program" className="block text-sm/6 text-zinc-950 font-semibold">
              I confirm my organization is <span className="font-semibold">not currently enrolled</span> in the Google Cloud for Startups Program.
            </label>
            <Text className="text-sm text-gray-500">
              Check your status or credits at the <a href="https://console.cloud.google.com/cloudstart/overview" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 underline inline-flex items-center gap-1">Google Cloud for Startups portal <ArrowTopRightOnSquareIcon className="h-4 w-4"/></a>.
            </Text>
          </div>
          <Switch
            checked={notInStartupProgram}
            onChange={setNotInStartupProgram}
            color="cyan"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="pr-6">
            <label htmlFor="no-marketplace-subscriptions" className="block text-sm/6 text-zinc-950 font-semibold">
              I confirm I have <span className="font-semibold">no active Marketplace subscriptions</span> in my organization.
            </label>
            <Text className="text-sm text-gray-500">
              You can check for active Marketplace subscriptions in the <a href="https://console.cloud.google.com/marketplace/purchases" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 underline inline-flex items-center gap-1">Google Cloud Marketplace purchases section <ArrowTopRightOnSquareIcon className="h-4 w-4"/></a>.
            </Text>
          </div>
          <Switch
            checked={noMarketplaceSubscriptions}
            onChange={setNoMarketplaceSubscriptions}
            color="cyan"
          />
        </div>
      </div>

      <div className="rounded-md bg-yellow-50 p-4 mt-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Need Help or Have CUDs/Startup Credits/Marketplace Subscriptions?
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                If you have active CUDs, Marketplace subscriptions, are part of the Startup Program, or are unsure, please <span className="font-semibold">do not proceed</span> with this automated transfer. Contact our team at <a href="mailto:sales@kloudstax.com" className="font-medium underline">sales@kloudstax.com</a>, and we will assist you manually.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthorizeAccess = ({ onAuthorized, onError }) => {
  const { ensureCredential } = useAuth();
  const auth = getAuth();
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isCheckingToken, setIsCheckingToken] = useState(true);

  // Required scopes for the project transfer process
  const requiredScopes = [
    'https://www.googleapis.com/auth/cloud-platform',
    'https://www.googleapis.com/auth/cloud-billing'
  ];

  // Check if current token already has the necessary scopes
  useEffect(() => {
    const checkExistingToken = async () => {
      setIsCheckingToken(true);
      try {
        if (!auth.currentUser) {
          throw new Error('User not logged in');
        }

        // Get the current ID token result which includes Firebase claims
        const tokenResult = await auth.currentUser.getIdTokenResult();
        
        // Try to get a fresh token without prompting to check if scopes exist
        await auth.currentUser.getIdToken(true);
        
        // If the user has a Google OAuth credential, try to check its scopes
        // Note: Firebase doesn't easily expose OAuth scopes, so we'll make a test request
        const testToken = await auth.currentUser.getIdToken();
        
        try {
          // Try a simple request to check if we have proper access
          const testResponse = await fetch('https://cloudresourcemanager.googleapis.com/v3/organizations:search', {
            headers: { 'Authorization': `Bearer ${testToken}` },
          });
          
          // If we get a 401 or 403, we need to re-auth for scopes
          // But if we get a 200 or even a 404 (not found), we likely have proper auth 
          if (testResponse.status !== 401 && testResponse.status !== 403) {
            // We have a token with sufficient privileges - continue
            console.log("Existing token has sufficient scopes");
            onAuthorized({
              accessToken: testToken,
              // Include other properties expected by downstream components
            });
            return;
          }
        } catch (e) {
          console.log("Error checking token, will re-authenticate:", e);
          // Continue to re-authentication flow
        }
        
        setIsCheckingToken(false);
      } catch (error) {
        console.error("Error checking existing token:", error);
        setIsCheckingToken(false);
      }
    };
    
    checkExistingToken();
  }, [auth, onAuthorized]);

  const handleAuthorize = async () => {
    setIsAuthorizing(true);
    setAuthError(null);
    try {
      // Create provider with required scopes
      const provider = new GoogleAuthProvider();
      requiredScopes.forEach(scope => provider.addScope(scope));
      
      // Only force re-prompt if necessary (removed 'prompt: consent')
      // This allows Google to skip the consent screen if user already granted these scopes
      
      // Use reauthenticateWithPopup to get a fresh credential with new scopes
      const result = await reauthenticateWithPopup(auth.currentUser, provider);
      
      if (result?.user) {
        // Get fresh credential with new scopes
        const credential = GoogleAuthProvider.credentialFromResult(result);
        onAuthorized(credential);
      } else {
        throw new Error('Authorization cancelled or failed.');
      }
    } catch (error) {
      console.error("Authorization Error:", error);
      // Handle errors...
      setAuthError(error.message || 'Authentication failed');
      onError(error.message);
    } finally {
      setIsAuthorizing(false);
    }
  };

  if (isCheckingToken) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center gap-2 text-gray-500 py-4">
          <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          <span>Checking authorization status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Text className="text-gray-600">
        To proceed, we need your permission to access certain Google Cloud information on your behalf. This allows us to verify your organization status, update necessary permissions, list your projects, and switch their billing accounts.
      </Text>
      <Subheading>Permissions Requested:</Subheading>
      <ul className="list-disc space-y-2 pl-5 text-gray-600">
        <li>
          <span className="font-semibold">View Google Cloud resources:</span> To identify your organization, projects, and billing accounts.
        </li>
        <li>
          <span className="font-semibold">Manage Google Cloud Billing:</span> To update project billing accounts to your new KloudStax-managed account.
        </li>
        <li>
          <span className="font-semibold">Manage Organization IAM Policies:</span> To grant required roles to ensure you have visibility into all projects.
        </li>
      </ul>
      <Text className="text-sm text-gray-500">
          We only use these permissions for the project transfer process outlined here. You can revoke these permissions later via your Google Account settings.
      </Text>

      {authError && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Authorization Failed
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{authError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-center">
        <Button 
          onClick={handleAuthorize} 
          disabled={isAuthorizing}
          className="bg-black text-white hover:bg-black cursor-pointer hover:text-cyan-300 disabled:opacity-50"
          color="black"
        >
          <LockClosedIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          {isAuthorizing ? 'Authorizing...' : 'Authorize & Continue'}
        </Button>
      </div>
    </div>
  );
};

const OrganizationSelector = ({ credential, onOrgSelected, onError }) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [selectedBillingAccount, setSelectedBillingAccount] = useState(null);
  const [isLoadingBillingAccounts, setIsLoadingBillingAccounts] = useState(false);
  const [billingAccountError, setBillingAccountError] = useState(null);

  useEffect(() => {
    if (!credential || billingAccounts.length > 0) return;

    const fetchOrganizations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = credential.accessToken;
        
        // Use the fetchAllPages helper for organizations
        const organizationsData = await fetchAllPages(
          'https://cloudresourcemanager.googleapis.com/v3/organizations:search',
          accessToken,
          'organizations',
          {}, // No additional query params
          (org) => org.name // Deduplication function to extract unique organization name
        );
        
        if (!organizationsData || organizationsData.length === 0) {
          throw new Error('No Google Cloud Organizations found for this user.');
        }
        
        // Process organizations to extract display name and ID
        const orgs = sortArrayOfObjectsCaseInsensitive(organizationsData.map(org => ({
          id: org.name.split('/')[1],
          displayName: org.displayName || `Organization ${org.name.split('/')[1]}`,
          name: org.name
        })), 'displayName');
        
        setOrganizations(orgs);
        
        // If only one organization, auto-select it
        if (orgs.length === 1) {
          setSelectedOrg(orgs[0]);
          // Don't call onOrgSelected here anymore since we need both org and billing account
        }
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setError(err.message);
        onError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchBillingAccounts = async () => {
      setIsLoadingBillingAccounts(true);
      setBillingAccountError(null);
      try {
        const accessToken = credential.accessToken;
        
        // Fetch all billing accounts
        const allBillingAccounts = await fetchAllPages(
          'https://cloudbilling.googleapis.com/v1/billingAccounts',
          accessToken,
          'billingAccounts',
          {}, // No additional query params
          (account) => account.name // Deduplication function
        );
        
        // Filter client-side for active KloudStax-managed accounts. for now we only allow direct resold, not TDSynnex.
        const kloudstaxAccounts = allBillingAccounts.filter(account => 
          account.open && 
          account.masterBillingAccount === kloudstaxMasterBillingAccount
        ) || [];
        
        if (kloudstaxAccounts.length === 0) {
          throw new Error('No active KloudStax managed billing accounts found. Please ensure your enrollment is complete.');
        }
        
        // Sort accounts by display name
        const sortedAccounts = sortArrayOfObjectsCaseInsensitive(kloudstaxAccounts, 'displayName');
        setBillingAccounts(sortedAccounts);
        
        // If only one billing account, auto-select it
        if (sortedAccounts.length === 1) {
          setSelectedBillingAccount(sortedAccounts[0]);
        }
      } catch (err) {
        console.error("Error fetching billing accounts:", err);
        setBillingAccountError(err.message);
      } finally {
        setIsLoadingBillingAccounts(false);
      }
    };

    fetchOrganizations();
    fetchBillingAccounts();
  }, [credential, onError, billingAccounts.length]);

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    if (selectedBillingAccount) {
      onOrgSelected(org.id, org.displayName, selectedBillingAccount);
    }
  };

  const handleBillingAccountSelect = (account) => {
    setSelectedBillingAccount(account);
    if (selectedOrg) {
      onOrgSelected(selectedOrg.id, selectedOrg.displayName, account);
    }
  };

  if (isLoading || isLoadingBillingAccounts) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-500 py-4">
        <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
        <span>Loading organizations and billing accounts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Failed to load organizations
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
              <p className="mt-1">Please ensure you have access to at least one Google Cloud Organization.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (billingAccountError) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Failed to load KloudStax billing accounts
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{billingAccountError}</p>
              <p className="mt-1">Please ensure your KloudStax enrollment is complete and you have access to KloudStax billing accounts.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      <div className="mt-4">
        <div className="rounded-md bg-black p-4 my-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-md font-normal text-white">
                {organizations.length === 1 ? 'You have access to one Google Cloud Organization.' : 'You have access to multiple Google Cloud Organizations. Select the organization that has your billable projects.'}<br /><br />
                If you do not see the organization you want to use, ask your administrator to add you as an Organization Admin to the organization you want to use.
              </h3>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Organization</label>
            <div className="w-full md:w-1/2">
              <Dropdown>
                <DropdownButton outline className="w-full font-normal justify-between bg-white border border-gray-500 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                  {selectedOrg ? selectedOrg.displayName : 'Choose an organization'}
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                </DropdownButton>
                <DropdownMenu className="min-w-48">
                  {organizations.map(org => (
                    <DropdownItem key={org.id} onClick={() => handleOrgSelect(org)}>
                      <DropdownLabel>{org.displayName}</DropdownLabel>
                      <DropdownDescription>Organization ID: {org.id}</DropdownDescription>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select KloudStax Billing Account</label>
            <div className="w-full md:w-1/2">
              <Dropdown>
                <DropdownButton outline className="w-full font-normal justify-between bg-white border border-gray-500 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                  {selectedBillingAccount ? selectedBillingAccount.displayName : 'Choose a billing account'}
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                </DropdownButton>
                <DropdownMenu className="min-w-48">
                  {billingAccounts.map(account => (
                    <DropdownItem key={account.name} onClick={() => handleBillingAccountSelect(account)}>
                      <DropdownLabel>{account.displayName}</DropdownLabel>
                      <DropdownDescription>Billing Account ID: {account.name.split('/')[1]}</DropdownDescription>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      {selectedOrg && selectedBillingAccount && (
        <>
          <div className="rounded-md bg-white p-4 border border-gray-200">
            <h3 className="text-md font-medium">Selected Configuration</h3>
            <p className="text-sm text-gray-500 mt-2">Organization: {selectedOrg.displayName} (ID: {selectedOrg.id})</p>
            <p className="text-sm text-gray-500">Billing Account: {selectedBillingAccount.displayName} (ID: {selectedBillingAccount.name.split('/')[1]})</p>
          </div>
          <div className="rounded-md bg-cyan-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <InformationCircleIcon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-cyan-800">
                  Great!
                </h3>
                <div className="mt-2 text-sm text-cyan-700">
                  <p>
                    We'll use this organization and KloudStax billing account to align your projects.
                    Click "Next" to continue.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const UpdatePermissions = ({ credential, onComplete, onError, registerInstance, selectedOrgId }) => {
  const requiredRoles = ['roles/billing.admin', 'roles/owner'];
  const auth = getAuth();
  const userEmail = auth.currentUser?.email;

  const initialTasks = {
    // If selectedOrgId exists, we can skip the first step
    ...(selectedOrgId ? {} : {getOrg: { name: 'Fetch your organization details', status: 'pending', error: null }}),
    verifyAdmin: { name: 'Verify you are an Organization Admin', status: 'pending', error: null },
    // checkKloudstaxBA: { name: 'Verify you have access to your KloudStax Billing Account', status: 'pending', error: null },
    grantRoles: { name: 'Add necessary IAM roles to your account (Owner, Billing Admin)', status: 'pending', error: null },
    removeRoles: { name: 'Remove unnecessary IAM roles for the organization (Billing Account Creator)', status: 'pending', error: null },
  };

  const [tasks, setTasks] = useState(initialTasks);
  const [overallError, setOverallError] = useState(null);
  const [orgId, setOrgId] = useState(selectedOrgId || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!credential || !userEmail) {
      setOverallError("Missing user credentials. Please go back and authorize.");
      onError("Missing user credentials.");
    }
  }, [credential, userEmail, onError]);
  
  // Register this component's instance with the parent component
  useEffect(() => {
    if (registerInstance) {
      registerInstance({
        runPermissionTasks
      });
    }
  }, [registerInstance]);

  const updateTaskStatus = (taskId, status, error = null) => {
    setTasks(prev => ({ ...prev, [taskId]: { ...prev[taskId], status, error } }));
  };

  const runPermissionTasks = async () => {
    setIsProcessing(true);
    const accessToken = credential.accessToken;
    let currentOrgId = orgId;

    try {
      // 1. Get Organization ID (only if not already provided)
      if (!currentOrgId) {
        updateTaskStatus('getOrg', 'running');
        const orgSearchResponse = await fetch('https://cloudresourcemanager.googleapis.com/v3/organizations:search', {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (!orgSearchResponse.ok) throw new Error(`Organization Search Failed: ${orgSearchResponse.statusText}`);
        const orgData = await orgSearchResponse.json();
        if (!orgData.organizations || orgData.organizations.length === 0) {
          throw new Error('No Google Cloud Organization found for this user.');
        }
        currentOrgId = orgData.organizations[0].name.split('/')[1]; 
        setOrgId(currentOrgId);
        updateTaskStatus('getOrg', 'success');
      }

      // 2. Verify Org Admin Role
      updateTaskStatus('verifyAdmin', 'running');
      const getPolicyUrl = `https://cloudresourcemanager.googleapis.com/v3/organizations/${currentOrgId}:getIamPolicy`;
      const getPolicyResponse = await fetch(getPolicyUrl, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
          method: 'POST',
          body: JSON.stringify({ options: { requestedPolicyVersion: 3 } })
      });
      if (!getPolicyResponse.ok) throw new Error(`Get IAM Policy Failed: ${getPolicyResponse.statusText}`);
      const currentPolicy = await getPolicyResponse.json();
      const isAdmin = currentPolicy.bindings?.some(binding => 
         binding.role === 'roles/resourcemanager.organizationAdmin' && 
         binding.members?.includes(`user:${userEmail}`)
      );
      if (!isAdmin) {
        throw new Error('User does not have Organization Admin role.');
      }
      updateTaskStatus('verifyAdmin', 'success');

      // 3. Grant Required Roles (Owner, Billing Admin)
      updateTaskStatus('grantRoles', 'running');
      let policyNeedsUpdate = false;
      const userMember = `user:${userEmail}`;
      const updatedPolicy = JSON.parse(JSON.stringify(currentPolicy)); 
      if (!updatedPolicy.bindings) updatedPolicy.bindings = [];

      for (const role of requiredRoles) {
          let binding = updatedPolicy.bindings.find(b => b.role === role);
          if (!binding) {
              binding = { role: role, members: [] };
              updatedPolicy.bindings.push(binding);
          }
          if (!binding.members.includes(userMember)) {
              binding.members.push(userMember);
              policyNeedsUpdate = true;
          }
      }

      if (policyNeedsUpdate) {
          const setPolicyUrl = `https://cloudresourcemanager.googleapis.com/v3/organizations/${currentOrgId}:setIamPolicy`;
          const setPolicyResponse = await fetch(setPolicyUrl, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ policy: updatedPolicy, updateMask: 'bindings' })
          });
          if (!setPolicyResponse.ok) {
             const errorBody = await setPolicyResponse.text();
             console.error("Set IAM Policy Error Body:", errorBody);
             throw new Error(`Set IAM Policy Failed: ${setPolicyResponse.statusText}`);
          }
      }
      updateTaskStatus('grantRoles', 'success');
 
      // 4. Remove Billing Account Creator Role
      updateTaskStatus('removeRoles', 'running');
      const billingAccountCreatorRole = 'roles/billing.creator';
      let removeRolePolicyNeedsUpdate = false;
      
      // Get the most current policy again to ensure we have the latest
      const getLatestPolicyResponse = await fetch(getPolicyUrl, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
          method: 'POST',
          body: JSON.stringify({ options: { requestedPolicyVersion: 3 } })
      });
      if (!getLatestPolicyResponse.ok) throw new Error(`Get Latest IAM Policy Failed: ${getLatestPolicyResponse.statusText}`);
      const latestPolicy = await getLatestPolicyResponse.json();
      
      // Find the binding for Billing Account Creator role
      const creatorRoleBinding = latestPolicy.bindings?.find(binding => binding.role === billingAccountCreatorRole);
      
      if (creatorRoleBinding && creatorRoleBinding.members && creatorRoleBinding.members.length > 0) {
          // Remove the role binding entirely or remove all members
          const index = latestPolicy.bindings.findIndex(binding => binding.role === billingAccountCreatorRole);
          if (index !== -1) {
              latestPolicy.bindings.splice(index, 1);
              removeRolePolicyNeedsUpdate = true;
          }
      }
      
      if (removeRolePolicyNeedsUpdate) {
          const setRemovePolicyUrl = `https://cloudresourcemanager.googleapis.com/v3/organizations/${currentOrgId}:setIamPolicy`;
          const setRemovePolicyResponse = await fetch(setRemovePolicyUrl, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ policy: latestPolicy, updateMask: 'bindings' })
          });
          if (!setRemovePolicyResponse.ok) {
              const errorBody = await setRemovePolicyResponse.text();
              console.error("Remove Role Policy Error Body:", errorBody);
              throw new Error(`Remove Billing Account Creator Role Failed: ${setRemovePolicyResponse.statusText}`);
          }
      }
      updateTaskStatus('removeRoles', 'success');

      setIsComplete(true);
      onComplete(currentOrgId);

    } catch (error) {
      console.error("Permission Task Error:", error);
      const failedTask = Object.entries(tasks).find(([_, task]) => task.status === 'running')?.[0];
      if (failedTask) {
        updateTaskStatus(failedTask, 'failed', error.message);
      }
      setOverallError(`Error during: ${tasks[failedTask || 'getOrg'].name}. ${error.message}. Please ensure you have the necessary permissions or contact support.`);
      onError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Text>First, we'll take the following actions to ensure you have the necessary permissions to transfer your projects:</Text>
      <ul className="space-y-3">
        {Object.entries(tasks).map(([id, task]) => (
          <li key={id} className="flex items-center gap-3">
            <StatusIcon status={task.status} />
            <span className={`text-sm ${task.status === 'failed' ? 'text-red-600' : 'text-gray-700'}`}>
              {task.name}
            </span>
            {task.error && <span className="text-xs text-red-500">({task.error})</span>}
          </li>
        ))}
      </ul>
      
      {isProcessing && (
        <div className="flex items-center justify-center gap-2 text-gray-500 mt-6">
          <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          <span>Updating permissions...</span>
        </div>
      )}
      
      {overallError && (
         <div className="rounded-md bg-red-50 p-4 mt-4">
           <div className="flex">
             <div className="ml-3">
               <h3 className="text-sm font-medium text-red-800">
                 Permission Update Failed
               </h3>
               <div className="mt-2 text-sm text-red-700">
                 <p>{overallError}</p>
                 <p className="mt-1">You might need higher privileges in your Google Cloud Organization or need to contact support.</p>
               </div>
             </div>
           </div>
         </div>
      )}
      
      {isComplete && (
        <div className="rounded-md bg-green-50 p-4 mt-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Permissions updated successfully
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>All necessary permissions have been configured. You can now proceed to the next step.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SelectProjects = ({ credential, orgId, onProjectsSelected, targetBillingAccount }) => {
    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showIgnoredProjects, setShowIgnoredProjects] = useState(false); // State to toggle visibility

    const fetchBillingInfo = useCallback(async (projectId, accessToken) => {
        try {
            const response = await fetch(`https://cloudbilling.googleapis.com/v1/projects/${projectId}/billingInfo`, {
                headers: { 'Authorization': `Bearer ${accessToken}` },
            });
            if (response.status === 404 || response.status === 403) {
                 console.warn(`No billing info found or accessible for project ${projectId}`);
                return { billingAccountName: 'N/A', billingEnabled: false };
            }
            if (!response.ok) {
                throw new Error(`Failed to fetch billing info for ${projectId}: ${response.statusText}`);
            }
            return await response.json();
        } catch (err) {
            console.error(`Error fetching billing info for ${projectId}:`, err);
            return { billingAccountName: 'Error', billingEnabled: false, error: err.message }; 
        }
    }, []);


    useEffect(() => {
        if (!credential || !orgId) return;

        const loadProjects = async () => {
            setIsLoading(true);
            setError(null);
            const accessToken = credential.accessToken;
            let fetchedProjects = {};  // Use an object to avoid duplicates

            try {
                // 1. Fetch all billing accounts to get display names
                const billingAccounts = await fetchAllPages(
                    'https://cloudbilling.googleapis.com/v1/billingAccounts',
                    accessToken,
                    'billingAccounts',
                    {}, // No additional query params
                    (account) => account.name // Deduplication function
                );
                
                const billingAccountsMap = {};
                
                // Create a map of billing account ID to display name
                billingAccounts.forEach(account => {
                    billingAccountsMap[account.name] = account.displayName || 'No Name';
                });

                // 2. For each billing account, fetch associated projects
                const billingProjectPromises = billingAccounts.map(async (account) => {
                    try {
                        console.log("Account:", account);
                        if (account.masterBillingAccount === kloudstaxMasterBillingAccount || account.masterBillingAccount === tdsynnexMasterBillingAccount) {
                            return [];
                        }
                        const billingAccountId = account.name.split('/')[1];
                        const projectBillingInfos = await fetchAllPages(
                            `https://cloudbilling.googleapis.com/v1/billingAccounts/${billingAccountId}/projects`,
                            accessToken,
                            'projectBillingInfo'
                        );
                        
                        return projectBillingInfos.map(projectBilling => {
                            const projectId = projectBilling.projectId;
                            // Store billing info along with project ID
                            return {
                                projectId,
                                billingInfo: projectBilling,
                                billingAccountName: projectBilling.billingAccountName,
                                billingAccountId: projectBilling.billingAccountName?.split('/')[1] || 'N/A',
                                billingDisplayName: billingAccountsMap[projectBilling.billingAccountName] || 'Unknown Account',
                                billingEnabled: projectBilling.billingEnabled
                            };
                        });
                    } catch (err) {
                        console.warn(`Error fetching projects for billing account ${account.name}:`, err);
                        return [];
                    }
                });
                
                // Wait for all billing account project fetches to complete
                const billingProjectsLists = await Promise.all(billingProjectPromises);
                
                // Flatten and index all projects from billing accounts by projectId
                const billingProjects = {};
                billingProjectsLists.flat().forEach(project => {
                    billingProjects[project.projectId] = project;
                });
                
                // 3. Fetch all projects to catch any that weren't associated with billing accounts in the organization. This could mean a) billing disabled, or b) billing is enabled but the accoutn is outside the organization.
                const allProjects = await fetchAllPages(
                    'https://cloudresourcemanager.googleapis.com/v1/projects',
                    accessToken,
                    'projects'
                );
                
                // Filter active projects
                const activeProjects = allProjects.filter(p => 
                    p.lifecycleState === 'ACTIVE' && !p.projectId.startsWith('sys-')
                );
                
                // 4. For projects not already captured, fetch billing info individually
                const remainingProjectPromises = activeProjects
                    .filter(p => !billingProjects[p.projectId])
                    .map(async project => {
                        try {
                            const billingInfo = await fetchBillingInfo(project.projectId, accessToken);
                            const billingAccountName = billingInfo?.billingAccountName || 'N/A';
                            const billingAccountId = billingAccountName !== 'N/A' ? billingAccountName.split('/')[1] || 'N/A' : 'N/A';
                            const billingDisplayName = billingAccountsMap[billingAccountName] || 'Unknown Account';
                            
                            return {
                                ...project,
                                billingInfo,
                                billingAccountId,
                                billingDisplayName,
                                billingEnabled: billingInfo?.billingEnabled || false
                            };
                        } catch (err) {
                            console.warn(`Error fetching billing info for ${project.projectId}:`, err);
                            return {
                                ...project,
                                billingInfo: { error: err.message },
                                billingAccountId: 'N/A',
                                billingDisplayName: 'Error',
                                billingEnabled: false
                            };
                        }
                    });
                
                const remainingProjects = await Promise.all(remainingProjectPromises);
                
                // 5. Combine projects from billing accounts with remaining projects
                const combinedProjects = [...Object.values(billingProjects)];
                
                // Add full project details to the projects from billing accounts
                activeProjects.forEach(project => {
                    if (billingProjects[project.projectId]) {
                        // Merge the project info with the billing info
                        combinedProjects.forEach((p, index) => {
                            if (p.projectId === project.projectId) {
                                combinedProjects[index] = {
                                    ...project,
                                    ...combinedProjects[index]
                                };
                            }
                        });
                    }
                });
                
                // Add remaining projects
                remainingProjects.forEach(project => {
                    if (!billingProjects[project.projectId]) {
                        combinedProjects.push(project);
                    }
                });
                 
                console.log("Combined Projects:", combinedProjects);
                setProjects(combinedProjects); 

                // Initialize selection state for projects with billing enabled
                const initialSelection = combinedProjects.reduce((acc, proj) => {
                  console.log("Project:", proj);
                  console.log("Target Billing Account:", targetBillingAccount);
                    if (proj.billingEnabled && proj.billingAccountId !== 'N/A' && `billingAccounts/${proj.billingAccountId}` !== targetBillingAccount.name) {
                        acc[proj.projectId] = true;
                    }
                    return acc;
                }, {});
                
                setSelectedProjects(initialSelection);
                onProjectsSelected(Object.keys(initialSelection).filter(id => initialSelection[id]));

            } catch (err) {
                console.error("Error loading projects:", err);
                setError(`Failed to load projects: ${err.message}`);
                setProjects([]);
                setSelectedProjects({});
            } finally {
                setIsLoading(false);
            }
        };

        loadProjects();
    }, [credential, orgId, fetchBillingInfo, onProjectsSelected, targetBillingAccount]);

    const handleToggleChange = (projectId) => {
         const newSelection = { ...selectedProjects, [projectId]: !selectedProjects[projectId] };
         setSelectedProjects(newSelection);
         onProjectsSelected(Object.keys(newSelection).filter(id => newSelection[id]));
     };

    if (isLoading) {
        return (
             <div className="flex items-center justify-center gap-2 text-gray-500">
                  <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                  <span>Loading projects...</span>
              </div>
        );
    }

    if (error) {
        return (
             <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                    <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error Loading Projects</h3>
                    <div className="mt-2 text-sm text-red-700"><p>{error}</p></div>
                    </div>
                </div>
             </div>
        );
    }

     if (projects.length === 0) {
         return <Text>No active projects found in your organization.</Text>;
     }

    const billableProjects = projects.filter(p => p.billingInfo?.billingEnabled && p.billingAccountId !== 'N/A');
    const nonBillableProjects = projects.filter(p => (!p.billingInfo?.billingEnabled || p.billingAccountId === 'N/A') && !p.billingInfo?.error);
    const projectsWithErrors = projects.filter(p => p.billingInfo?.error );

    const toggleIgnoredProjects = () => {
        setShowIgnoredProjects(!showIgnoredProjects);
    };

    return (
        <div className="space-y-6">
            {targetBillingAccount && (
                <div className="rounded-md border border-black bg-black p-4 mt-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <InformationCircleIcon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3 flex-1">
                            <h3 className="text-md font-medium text-white">Let's switch your projects to use your KloudStax Billing Account</h3>
                            <div className="mt-2 text-sm text-white">
                                <p><span className="font-medium text-gray-400">Account Name:</span> <span className="text-white">{targetBillingAccount.displayName}</span></p>
                                <p><span className="font-medium text-gray-400">Account ID:</span> <span className="text-white">{targetBillingAccount.name.split('/')[1]}</span></p>
                                <p><span className="font-medium text-gray-400">Account Link:</span> <a href={`https://console.cloud.google.com/billing/${targetBillingAccount.name.split('/')[1]}`} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-600 inline-flex items-center">View in Console <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" aria-hidden="true" /></a></p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            

             {billableProjects.length > 0 && (
                 <fieldset className="space-y-4">
                     <Heading className="text-base font-semibold leading-6 text-gray-900">Choose Projects to Switch</Heading>
                     <Text>In general, we recommend switching all projects to the KloudStax Billing Account. If you have projects that you do not want to switch, you can unselect them.</Text>
                     {billableProjects.map((project) => (
                         <div key={project.projectId} className="flex items-center justify-between border-b border-gray-100 pb-3">
                             <div>
                                 <div className="font-medium">
                                     {project.name || project.projectId} <span className="text-xs text-gray-500">({project.projectId})</span>
                                 </div>
                                 <div className="text-xs text-gray-600">
                                 {`billingAccounts/${project.billingAccountId}` == targetBillingAccount.name && (<span className="font-bold italic mr-2">Already transferred</span>)}                                                                    
                                     Current Billing Account: {project.billingDisplayName} <span className="text-gray-400">({project.billingAccountId})</span>
                                 </div>
                             </div>
                             <Switch
                                 checked={!!selectedProjects[project.projectId]}
                                 onChange={() => handleToggleChange(project.projectId)}
                                 color="cyan"
                             />
                         </div>
                     ))}
                 </fieldset>
             )}

             {projectsWithErrors.length > 0 && (
                  <div className="mt-2 bg-gray-50 p-4 rounded-md">
                      <span className="text-md">Projects with Errors</span>
                      <div className="flex">
                          <div className="flex-shrink-0">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                          </div>
                          <div className="ml-3 flex-1 md:flex md:justify-between">
                              <p className="text-sm text-gray-600">
                                  These projects had errors when fetching billing information. 
                              </p>
                          </div>
                      </div>
                      <ul className="mt-2 divide-y divide-gray-200 text-sm ">
                          {projectsWithErrors.map(project => (
                            <li key={project.projectId} className="py-2">
                                  {project.name || project.projectId} <span className="text-xs text-gray-500">({project.projectId})</span> -
                                  <span className="ml-1 text-xs text-gray-600">
                                      {` Error fetching billing: ${project.billingInfo.error}`}
                                  </span>
                          </li>
                          ))}
                      </ul>
                  </div>
          
                 
               
             )}

             {/* Show projects without active billing */}
             
             {nonBillableProjects.length > 0 && (
                 <div className="mt-6">
                     <button 
                         onClick={toggleIgnoredProjects}
                         className="text-cyan-600 hover:text-cyan-800 text-sm flex items-center gap-1"
                     >
                         <span>{showIgnoredProjects ? 'Hide' : 'Show'} {nonBillableProjects.length} ignored project{nonBillableProjects.length === 1 ? '' : 's'}</span>
                         <svg 
                             xmlns="http://www.w3.org/2000/svg" 
                             width="16" 
                             height="16" 
                             viewBox="0 0 24 24" 
                             fill="none" 
                             stroke="currentColor" 
                             strokeWidth="2" 
                             strokeLinecap="round" 
                             strokeLinejoin="round"
                             className={`transition-transform ${showIgnoredProjects ? 'rotate-180' : ''}`}
                         >
                             <polyline points="6 9 12 15 18 9"></polyline>
                         </svg>
                     </button>
                     
                     {showIgnoredProjects && (
                         <div className="mt-2 bg-gray-50 p-4 rounded-md">
                             <span className="text-md">Projects without Active Billing</span>
                             <div className="flex">
                                 <div className="flex-shrink-0">
                                     <InformationCircleIcon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
                                 </div>
                                 <div className="ml-3 flex-1 md:flex md:justify-between">
                                     <p className="text-sm text-gray-600">
                                         These projects will be skipped during the transfer process. This is normal as we only need to set projects with active billing.
                                     </p>
                                 </div>
                             </div>
                             <ul className="mt-2 divide-y divide-gray-200 text-sm ">
                                 {nonBillableProjects.map(project => (
                                    <li key={project.projectId} className="py-2">
                                         {project.name || project.projectId} <span className="text-xs text-gray-500">({project.projectId})</span> -
                                         <span className="ml-1 text-xs text-gray-600">
                                             {project.billingInfo?.error ? ` Error fetching billing: ${project.billingInfo.error}` : ' Billing not enabled'}
                                         </span>
                                 </li>
                                 ))}
                             </ul>
                         </div>
                     )}
                 </div>
             )}
        </div>
    );
};

const SwitchBilling = ({ credential, projectIds, onComplete, onProcessingComplete, targetBillingAccount }) => {
    const [targetBillingAccountId, setTargetBillingAccountId] = useState(null);
    const [fetchBAError, setFetchBAError] = useState(null);
    const [projectStatuses, setProjectStatuses] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [errorCount, setErrorCount] = useState(0);

    // Initialize statuses
    useEffect(() => {
        setProjectStatuses(projectIds.reduce((acc, id) => {
            acc[id] = { status: 'pending', error: null };
            return acc;
        }, {}));
    }, [projectIds]);

    // Use the targetBillingAccount passed from props instead of fetching
    useEffect(() => {
        if (!targetBillingAccount) {
            setFetchBAError("No KloudStax billing account selected. Please go back and select a billing account.");
            return;
        }
        
        // Set the billing account ID from the passed account
        setTargetBillingAccountId(targetBillingAccount.name);
    }, [targetBillingAccount]);

    const updateProjectStatus = (projectId, status, error = null) => {
        setProjectStatuses(prev => {
            const newStatuses = { ...prev, [projectId]: { status, error } };
            return newStatuses;
        });
    };

    // Check for errors and update error state
    const updateErrorState = () => {
        // Use a slight delay to ensure all status updates have been applied
        setTimeout(() => {
            const errors = Object.values(projectStatuses).filter(s => s.status === 'failed').length;
            console.log(`Error check: Found ${errors} errors in project status`);
            setErrorCount(errors);
            setHasErrors(errors > 0);
        }, 50);
    };

    // Force update error UI whenever statuses change
    useEffect(() => {
        updateErrorState();
    }, [projectStatuses]);

    // Function to update billing for a single project
    const switchSingleProjectBilling = async (projectId, targetBA) => {
        updateProjectStatus(projectId, 'running');
        try {
            const accessToken = credential.accessToken;
            const url = `https://cloudbilling.googleapis.com/v1/projects/${projectId}/billingInfo`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    billingAccountName: targetBA,
                }),
            });

            if (!response.ok) {
                const errorBody = await response.json();
                console.error(`Billing switch failed for ${projectId}:`, errorBody);
                const message = errorBody?.error?.message || `HTTP ${response.status}: ${response.statusText}`;
                console.log(`Billing switch failed for ${projectId}:`, message);
                if (message?.toLowerCase().includes("precondition check failed")) {
                    throw new Error("Precondition failed: Project billing may be locked. If this project has a Marketplace subscription, cancel it before switching billing on this project.");
                }
                throw new Error(message);
            }
             
            updateProjectStatus(projectId, 'success');
            return { success: true, projectId };
        } catch (error) {
            console.error(`Error switching billing for ${projectId}:`, error);
            updateProjectStatus(projectId, 'failed', error.message);
            return { success: false, projectId, error: error.message };
        }
    };

    // Handle retry for a single project
    const handleRetry = async (projectId) => {
        if (!targetBillingAccountId) return;
        await switchSingleProjectBilling(projectId, targetBillingAccountId);
        updateErrorState();
        checkCompletionStatus();
    };

    // Check if all projects have been processed
    const checkCompletionStatus = () => {
        const allComplete = projectIds.every(id => 
            projectStatuses[id]?.status === 'success' || 
            projectStatuses[id]?.status === 'failed'
        );
        
        setIsComplete(allComplete);
        
        // Notify parent component of completion status and error count
        if (allComplete && onProcessingComplete) {
            // Count errors directly from current state after all processing
            const errors = Object.values(projectStatuses)
                .filter(s => s.status === 'failed')
                .length;
            
            console.log(`Completion check: Found ${errors} errors in ${projectIds.length} projects`);
            onProcessingComplete(true, errors);
        }
    };

    // Trigger the switch process when the component mounts and target BA is ready
    useEffect(() => {
        if (isProcessing || !targetBillingAccountId || projectIds.length === 0) {
            return;
        }

        const processAllProjects = async () => {
            setIsProcessing(true);
            setHasErrors(false);
            setErrorCount(0);
            
            const results = [];
            for (const projectId of projectIds) {
                if (projectStatuses[projectId]?.status === 'pending') {
                    const result = await switchSingleProjectBilling(projectId, targetBillingAccountId);
                    results.push(result);
                }
            }
            
            console.log("Billing switch results:", results);
            
            // Mark process as complete but don't auto-advance
            setIsComplete(true);
            setIsProcessing(false);
            
            // Check for any errors and count them
            const failedResults = results.filter(r => !r.success);
            const errorCount = failedResults.length;
            console.log(`Process completion: Found ${errorCount} failed results out of ${results.length}`);
            
            setProjectStatuses(prev => {
                // Force a rerender to ensure all statuses are visible
                return { ...prev };
            });
            
            // Extra delay to ensure state is updated before error check
            setTimeout(() => {
                // Double-check statuses to make sure we count all errors
                const currentFailedCount = Object.values(projectStatuses)
                    .filter(s => s.status === 'failed')
                    .length;
                
                const finalErrorCount = Math.max(errorCount, currentFailedCount);
                
                console.log(`Final error count: ${finalErrorCount}`);
                setErrorCount(finalErrorCount);
                setHasErrors(finalErrorCount > 0);
                
                // Notify parent component of completion status
                if (onProcessingComplete) {
                    onProcessingComplete(true, finalErrorCount);
                }
            }, 100);
        };

        processAllProjects();

    }, [targetBillingAccountId, projectIds, isProcessing, credential, onProcessingComplete]);

    if (fetchBAError) {
        return (
            <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                    <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error Finding Target Billing Account</h3>
                    <div className="mt-2 text-sm text-red-700"><p>{fetchBAError}</p></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!targetBillingAccountId && !fetchBAError) {
        return (
            <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                <span>Preparing billing account details...</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Target billing account information */}
            <div className="rounded-md border border-black bg-white p-4 mb-6">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-black">We will transfer these projects to your KloudStax Billing Account</h3>
                        <div className="mt-2 text-sm text-black">
                            <p><span className="font-medium">Account Name:</span> {targetBillingAccount?.displayName}</p>
                            <p><span className="font-medium">Account ID:</span> {targetBillingAccountId?.split('/')[1]}</p>
                        </div>
                    </div>
                </div>
            </div>

            <Text>Switching billing for {projectIds.length} project(s) to the KloudStax account shown above.</Text>
            
            <ul className="divide-y divide-gray-200">
                {projectIds.map(projectId => (
                    <li key={projectId} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                            <StatusIcon status={projectStatuses[projectId]?.status || 'pending'} />
                            <span className="text-sm text-gray-700">
                                {projectId}
                            </span>
                        </div>
                        
                        <div className="flex items-center">
                            {/* Right side status indicator */}
                            {projectStatuses[projectId]?.status === 'failed' && (
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="group relative"
                                        title={projectStatuses[projectId].error}
                                    >
                                        <XCircleIcon className="h-6 w-6 text-red-500" />
                                        <div className="absolute hidden group-hover:block bottom-full right-0 bg-black text-white text-xs p-2 rounded max-w-xs mb-1 z-10">
                                            {projectStatuses[projectId].error}
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleRetry(projectId)}
                                        className="rounded-full p-1 hover:bg-gray-100"
                                        title="Retry"
                                    >
                                        <ArrowPathIcon className="h-5 w-5 text-gray-600" />
                                    </button>
                                </div>
                            )}
                            {projectStatuses[projectId]?.status === 'running' && (
                                <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                            )}
                            {projectStatuses[projectId]?.status === 'success' && (
                                <CheckCircleIcon className="h-6 w-6 text-green-500" />
                            )}
                            {projectStatuses[projectId]?.status === 'pending' && (
                                <div className="size-5 rounded-full bg-gray-300" />
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            {hasErrors && (
                <div className="rounded-md bg-yellow-50 p-4 mt-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                                Some projects failed to transfer
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    Billing accounts for projects marked with an error above could not be switched automatically. 
                                    You can hover over the error icon to see detailed error information, or click the retry button to try again.
                                    <br/><br/>
                                    If you have any questions, or need help resolving these issues, please contact <a href="mailto:sales@kloudstax.com" className="font-medium underline">sales@kloudstax.com</a> for assistance.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Congratulations = ({ transferResults }) => {
    const successCount = transferResults?.successCount || 0;
    const failureCount = transferResults?.failureCount || 0;
    const totalCount = transferResults?.totalCount || 0;

    return (
        <div className="text-center space-y-6">
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
            <Heading>Billing Switch Completed {failureCount > 0 ? ' (with errors)' : ''}</Heading>
            <div className="py-4">
                 <p className="text-lg font-medium text-gray-900">Summary:</p>
                 <p>{successCount} project{successCount === 1 ? '' : 's'} switched to your KloudStax Billing Account.</p>
                 {failureCount > 0 && (
                      <>
                      <p className="text-red-600">{failureCount} project{failureCount === 1 ? '' : 's'} failed to switch automatically.</p>
                      <br/>
                      {/* <ul>
                        {transferResults?.failureDetails?.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul> */}
                      </>
                 )}
            </div>
             {failureCount > 0 && (
                 <Text className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-md">
                    For projects that failed, please check the previous step for error details. Manual intervention might be required due to factors like Marketplace subscriptions. Contact <a href="mailto:sales@kloudstax.com" className="font-medium underline">sales@kloudstax.com</a> if you need assistance.
                 </Text>
             )}
             <Text className="text-gray-500 text-sm">
                 You can now close this window or go back to your Google Cloud console.
             </Text>
         </div>
    );
};

const ProjectTransfer = () => {
  const [currentStep, setCurrentStep] = useState('prerequisites');
  const [prerequisitesMet, setPrerequisitesMet] = useState(false);
  const [gcpCredential, setGcpCredential] = useState(null); // Store the credential
  const [authError, setAuthError] = useState(null); // Store auth error
  const [permissionError, setPermissionError] = useState(null); // Store permission step error
  const [orgId, setOrgId] = useState(null); // Store Org ID after permissions step
  const [orgDisplayName, setOrgDisplayName] = useState(null); // Store Org display name
  const [selectedBillingAccount, setSelectedBillingAccount] = useState(null); // Store selected billing account
  const [projectsToTransfer, setProjectsToTransfer] = useState([]); // Store selected project IDs
  const [transferResults, setTransferResults] = useState([]); // Store results from SwitchBilling
  const [switchingComplete, setSwitchingComplete] = useState(false); // Track if switching is complete
  const [errorCount, setErrorCount] = useState(0); // Track number of errors
  const [permissionsStarted, setPermissionsStarted] = useState(false); // Track if permissions update started
  const [isComplete, setIsComplete] = useState(false); // Track if permission update is complete

  const handlePrerequisitesConfirm = (met) => {
    setPrerequisitesMet(met);
  };

  // Callback for successful authorization
  const handleAuthorizationSuccess = (credential) => {
    setGcpCredential(credential);
    setAuthError(null);
    setCurrentStep('selectOrg'); // Move to org selection step on auth success
  };

  // Callback for organization selection
  const handleOrgSelected = (selectedOrgId, displayName, billingAccount) => {
    setOrgId(selectedOrgId);
    setOrgDisplayName(displayName);
    setSelectedBillingAccount(billingAccount);
  };

  // Callback for organization selection error
  const handleOrgSelectionError = (error) => {
    setPermissionError(error);
  };

  // Callback for authorization error
  const handleAuthorizationError = (error) => {
    setAuthError(error);
    setGcpCredential(null);
    // Stay on the authorize step
  };

  // Callback for successful permission update
  const handlePermissionUpdateComplete = (fetchedOrgId) => {
      if (!orgId) {
        setOrgId(fetchedOrgId);
      }
      setPermissionError(null);
      setIsComplete(true);
      // Don't automatically advance - wait for user to click Next
  };

  // Callback for permission update error
  const handlePermissionUpdateError = (error) => {
      setPermissionError(error);
      // Stay on updateIAM step
  };

  // Callback to receive selected projects from SelectProjects component
  const handleProjectsSelected = useCallback((selectedIds) => {
      console.log("Selected project IDs:", selectedIds);
      setProjectsToTransfer(selectedIds);
  }, []); // Empty dependency array means this function is stable

  // Callback for when billing switch process finishes
  const handleBillingSwitchComplete = useCallback((results) => {
      console.log("Billing switch process completed:", results);
      setCurrentStep('congratulations');
  }, []);

  // New callback for when switching processing is complete but before moving to next step
  const handleSwitchingProcessingComplete = useCallback((complete, errors) => {
      console.log(`Switching complete: ${complete}, Errors: ${errors}`);
      setSwitchingComplete(complete);
      setErrorCount(errors);
      
      // Calculate successful count
      const successCount = projectsToTransfer.length - errors;
      console.log(`Success count: ${successCount} of ${projectsToTransfer.length} projects`);
      
      // Store success/error counts for the results screen
      setTransferResults({
          totalCount: projectsToTransfer.length,
          successCount: successCount,
          failureCount: errors,
          // We don't have the individual project results here, but we have the counts
      });
  }, [projectsToTransfer]);

  // Reference to UpdatePermissions component methods
  const updatePermissionsRef = React.useRef(null);

  const steps = {
    prerequisites: {
      name: 'Prerequisites Check',
      view: () => <PrerequisitesCheck onConfirm={handlePrerequisitesConfirm} prerequisitesMet={prerequisitesMet} />
    },
    authorize: {
      name: 'Authorize Access',
      // Pass callbacks to the AuthorizeAccess component
      view: () => <AuthorizeAccess onAuthorized={handleAuthorizationSuccess} onError={handleAuthorizationError} />
    },
    selectOrg: {
      name: 'Select Organization & Billing Account',
      view: () => <OrganizationSelector 
                   credential={gcpCredential} 
                   onOrgSelected={handleOrgSelected}
                   onError={handleOrgSelectionError}
                 />
    },
    updateIAM: {
      name: 'Verify Permissions',
      // Pass credential, callbacks and ref, plus selected orgId
      view: () => gcpCredential ? 
                  <UpdatePermissions 
                    credential={gcpCredential} 
                    onComplete={handlePermissionUpdateComplete} 
                    onError={handlePermissionUpdateError}
                    selectedOrgId={orgId}
                    ref={updatePermissionsRef}
                  /> : 
                  <Text>Waiting for authorization...</Text> 
    },
    selectProjects: {
       name: 'Quick Switch to your new KloudStax Billing Account', 
       view: () => (gcpCredential && orgId) ? 
                   <SelectProjects 
                     credential={gcpCredential} 
                     orgId={orgId} 
                     onProjectsSelected={handleProjectsSelected}
                     targetBillingAccount={selectedBillingAccount}
                   /> : 
                   <Text>Waiting for permissions update...</Text>
    },
    switchBilling: { 
        name: 'Switch Billing', 
        view: () => (gcpCredential && projectsToTransfer.length > 0) ? 
                    <SwitchBilling 
                        credential={gcpCredential} 
                        projectIds={projectsToTransfer}
                        targetBillingAccount={selectedBillingAccount}
                        onComplete={handleBillingSwitchComplete}
                        onProcessingComplete={handleSwitchingProcessingComplete}
                    /> : 
                     (projectsToTransfer.length === 0 ? 
                        <Text>No projects selected for transfer. Proceeding to summary.</Text> : 
                        <Text>Waiting for project selection...</Text>)
    },
    congratulations: { 
        name: 'Billing Switch Complete', 
        view: () => <Congratulations transferResults={transferResults} /> 
    },
  };

  const handleNext = () => {
    const stepKeys = Object.keys(steps);
    const currentIndex = stepKeys.indexOf(currentStep);

    if (currentStep === 'prerequisites' && !prerequisitesMet) return;
    
    if (currentStep === 'selectOrg' && (!orgId || !selectedBillingAccount)) return; // Prevent proceeding without org and billing account
    
    // Handle the UpdateIAM step specially
    if (currentStep === 'updateIAM') {
      if (orgId && !permissionsStarted) {
        // Start permissions update
        setPermissionsStarted(true);
        // Find the UpdatePermissions component and call its runPermissionTasks method
        steps.updateIAM.instance?.runPermissionTasks();
        return;
      }
      if (permissionsStarted && !isComplete) {
        // Wait for permissions task to complete
        return;
      }
    }
    
    // Auto-advance from authorize handled by handleAuthorizationSuccess
    if (currentStep === 'authorize') return; 

    if (currentStep === 'selectProjects' && projectsToTransfer.length === 0) { 
        // Optional: Prevent advancing if no projects are selected? Or show a warning?
        // For now, allow advancing even with zero projects.
        console.warn("Proceeding with zero projects selected for transfer.");
    }

    if (currentIndex < stepKeys.length - 1) {
      setCurrentStep(stepKeys[currentIndex + 1]);
    }
  };

  const handlePrevious = () => {
     const stepKeys = Object.keys(steps);
     const currentIndex = stepKeys.indexOf(currentStep);
     if (currentIndex > 0) {
       // Clear state when going back from certain steps?
       if (currentStep === 'selectOrg') {
         setGcpCredential(null); // Force re-auth if going back from org selection
       }
       if (currentStep === 'updateIAM') {
         setPermissionsStarted(false); // Reset permissions started flag
       }
       if (currentStep === 'selectProjects') {
           setProjectsToTransfer([]); // Clear selected projects when going back
       }
       if (currentStep === 'switchBilling') {
           // Reset switching state when going back
           setSwitchingComplete(false);
           setErrorCount(0);
       }
       setCurrentStep(stepKeys[currentIndex - 1]);
     }
   };

  // Determine if Next button should be shown for the current step
  const showNextButton = () => {
    if (currentStep === 'authorize') return false;
    return true;
  };

  // Get button text for the current step
  const getNextButtonText = () => {
    if (currentStep === 'selectOrg') {
      return (orgId && selectedBillingAccount) ? "Next" : "Please select organization and billing account";
    }
    
    if (currentStep === 'updateIAM') {
      if (permissionsStarted && !isComplete) {
        return "Processing...";
      }
      return isComplete ? "Next" : "Update Permissions";
    }
    
    if (currentStep === 'switchBilling') {
      if (!switchingComplete) {
        return "Processing...";  
      }
      return errorCount > 0 ? `Continue (${errorCount} errors)` : "Continue";
    }
    
    if (currentStep === 'selectProjects') {
      return `Switch ${projectsToTransfer.length} project${projectsToTransfer.length === 1 ? '' : 's'}`;
    }
    
    return 'Next';
  };

  // Get a reference to the UpdatePermissions component's runPermissionTasks method
  const registerUpdatePermissionsInstance = (instance) => {
    steps.updateIAM.instance = instance;
  };

  // Determine if next button should be disabled
  const isNextButtonDisabled = () => {
    if (currentStep === 'prerequisites' && !prerequisitesMet) return true;
    if (currentStep === 'selectOrg' && (!orgId || !selectedBillingAccount)) return true;
    if (currentStep === 'updateIAM' && permissionsStarted && !isComplete) return true;
    if (currentStep === 'switchBilling' && !switchingComplete) return true;
    if (currentStep === 'selectProjects' && projectsToTransfer.length === 0) return true;
    if (currentStep === 'congratulations') return true;
    return false;
  };

  return (
      // <PageLayout>
        <div className="mx-auto max-w-4xl p-10 rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          <Heading>{steps[currentStep].name}</Heading>
          {orgDisplayName && (currentStep === 'updateIAM' || currentStep === 'selectProjects' || currentStep === 'switchBilling') && (
            <div className="mt-2 text-sm text-gray-600">
              Organization: {orgDisplayName} ({orgId})
              {selectedBillingAccount && <span> | Billing Account: {selectedBillingAccount.displayName} ({selectedBillingAccount.name.split('/')[1]})</span>}
            </div>
          )}
          <div className="mt-6">
             {currentStep === 'updateIAM' ? 
               React.cloneElement(steps[currentStep].view(), { 
                 registerInstance: registerUpdatePermissionsInstance 
               }) : 
               steps[currentStep].view()}
          </div>

          {/* Conditional Navigation Buttons */}
          <div className="mt-10 flex justify-between border-t border-gray-200 pt-6">
            <Button 
              onClick={handlePrevious} 
              disabled={currentStep === 'prerequisites'}
              plain
            >
              Previous
            </Button>
            {showNextButton() && (
              <Button 
                onClick={handleNext} 
                disabled={isNextButtonDisabled()}
                className="bg-black text-white hover:bg-black cursor-pointer hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed"
                color="black"
              >
                 {getNextButtonText()}
              </Button>
            )}
          </div>
        </div>
      // </PageLayout>
  );
};

export default ProjectTransfer; 